import { MouseEvent, ReactNode } from "react";
import { Box, Table, Tbody, Td, Th, Thead, Tr } from "@chakra-ui/react";
import { ColorIcon, customEvent, Spacing } from "goi_common";
import { BaseStack, baseColors } from "goi_common/src/design-system";
import { EskyPriceData, EskyPriceItem, ParlorDetailProps } from "@/types/parlor";
import { getParlorPriceDisclosure, PRICE_SOURCE_NOTICE } from "@/utils/parlorPriceDisclosure";

const cellProps = {
  px: "12px",
  py: "12px",
  borderColor: baseColors.line_normal,
  textTransform: "none" as const,
  letterSpacing: "normal",
};

const headCellProps = {
  ...cellProps,
  py: "10px",
  bg: baseColors.fill_normal,
  color: baseColors.label_alternative_1,
  fontWeight: 500,
};

type AccordionTrigger = "acc_price_binso" | "acc_price_fee" | "acc_price_esky";

/**
 * 접힌 아코디언 한 칸. details/summary 라 JS 없이 동작하고, 접혀 있어도 내용이 처음 HTML 에
 * 그대로 있어 크롤러가 읽는다(가격 공개 SEO 실험의 전제). 이벤트는 펼칠 때만 보낸다.
 * 닫을 때까지 세면 "가격표를 펼쳐 본 사람 수" 가 부풀려진다.
 */
function PriceAccordion({
  parlorId,
  trigger,
  title,
  meta,
  children,
}: {
  parlorId: string;
  trigger: AccordionTrigger;
  title: string;
  meta: string;
  children: ReactNode;
}) {
  return (
    <Box
      as="details"
      border={`1px solid ${baseColors.line_normal}`}
      borderRadius="12px"
      overflow="hidden"
      bg="white"
      sx={{
        "& > summary::-webkit-details-marker": { display: "none" },
        "& .price-acc-chevron": { transform: "rotate(90deg)", transition: "transform 0.2s" },
        "&[open] .price-acc-chevron": { transform: "rotate(-90deg)" },
      }}
    >
      <Box
        as="summary"
        cursor="pointer"
        listStyleType="none"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap="12px"
        p="16px"
        onClick={(e: MouseEvent<HTMLElement>) => {
          const details = e.currentTarget.parentElement as HTMLDetailsElement | null;
          if (details?.open) return;
          customEvent({
            newGtm: {
              path: "parlor_detail",
              trigger_and_target: trigger,
              properties: { parlor_id: parlorId },
            },
          });
        }}
      >
        <Box>
          <h3 className="body_1_normal_sb">{title}</h3>
          <Box className="label_1_r" color={baseColors.label_assistive_2} pt="2px">
            {meta}
          </Box>
        </Box>
        <ColorIcon
          className="price-acc-chevron"
          name="icon-arrow-right-small-mono"
          color={baseColors.label_assistive_1}
          size={20}
        />
      </Box>
      <Box px="16px" pb="16px">
        {children}
      </Box>
    </Box>
  );
}

// 장례 흐름 순서. 여기 없는 대분류는 맨 뒤
const ESKY_GROUP_ORDER = [
  "식사",
  "음료",
  "주류",
  "제사음식",
  "제단",
  "영정사진",
  "조화/화환/헌화",
  "관",
  "수의",
  "유골함",
  "입관용품",
  "상주용품",
  "기타용품",
  "운구차량",
  "도우미",
  "주차비",
  "전화비",
  "기타",
];
// 품목 단위가 고른 대분류만 평균을 보여준다. 식사는 1인분과 1통이 섞여 평균이 왜곡된다
const ESKY_AVERAGE_GROUPS = new Set(["관", "수의", "유골함", "제단", "운구차량"]);

const toManwonLabel = (won: number) =>
  won >= 10000 ? `${Math.round(won / 10000).toLocaleString()}만원` : `${won.toLocaleString()}원`;

const formatKoreanDate = (date: string) => {
  const [y, m, d] = date.split("-");
  return `${y}년 ${Number(m)}월 ${Number(d)}일`;
};

/** e하늘 식사, 장례용품 표. 대분류별로 한 번 더 접는다 (많은 곳은 150개 넘음) */
function EskyPriceGroups({ data }: { data: EskyPriceData }) {
  const groups = new Map<string, EskyPriceItem[]>();
  data.items.forEach((item) => groups.set(item.group, [...(groups.get(item.group) || []), item]));
  const ordered = [...groups.entries()].sort(
    ([a], [b]) => (ESKY_GROUP_ORDER.indexOf(a) + 1 || 99) - (ESKY_GROUP_ORDER.indexOf(b) + 1 || 99),
  );

  return (
    <BaseStack spacing={6}>
      {ordered.map(([group, items]) => {
        const prices = items.map((i) => i.price);
        const min = Math.min(...prices);
        const max = Math.max(...prices);
        const subs = [...new Set(items.map((i) => i.sub))];
        return (
          <Box
            key={group}
            as="details"
            borderRadius="8px"
            bg={baseColors.fill_normal}
            sx={{
              "& > summary::-webkit-details-marker": { display: "none" },
              "& .esky-chevron": { transform: "rotate(90deg)", transition: "transform 0.2s" },
              "&[open] .esky-chevron": { transform: "rotate(-90deg)" },
            }}
          >
            <Box
              as="summary"
              cursor="pointer"
              listStyleType="none"
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap="8px"
              p="12px"
            >
              <Box>
                <Box as="span" className="body_2_normal_sb">
                  {group}
                </Box>
                <Box as="span" className="label_1_r" color={baseColors.label_assistive_2} pl="6px">
                  {items.length}개 품목,{" "}
                  {min === max
                    ? toManwonLabel(min)
                    : `${toManwonLabel(min)} ~ ${toManwonLabel(max)}`}
                </Box>
              </Box>
              <ColorIcon
                className="esky-chevron"
                name="icon-arrow-right-small-mono"
                color={baseColors.label_assistive_1}
                size={18}
              />
            </Box>
            <Box px="12px" pb="12px">
              {subs.map((sub) => {
                const average = ESKY_AVERAGE_GROUPS.has(group)
                  ? data.averages[sub ? `${group}/${sub}` : group]
                  : undefined;
                return (
                  <Box key={sub} pt="8px">
                    {(sub || average?.local) && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        className="label_1_m"
                        color={baseColors.label_alternative_1}
                        pb="4px"
                      >
                        <span>{sub || group}</span>
                        {average?.local ? (
                          <span>
                            관내 평균 {toManwonLabel(average.local)}
                            {average.national
                              ? `, 전국 평균 ${toManwonLabel(average.national)}`
                              : ""}
                          </span>
                        ) : null}
                      </Box>
                    )}
                    <Box bg="white" borderRadius="6px" overflow="hidden">
                      <Table size="sm" className="label_1_r">
                        <Tbody>
                          {items
                            .filter((i) => i.sub === sub)
                            .map((i, idx) => (
                              <Tr key={`${i.name}-${idx}`} _last={{ td: { borderBottom: "none" } }}>
                                <Td {...cellProps}>
                                  {i.name || sub || group}
                                  {i.note && (
                                    <Box
                                      className="caption_1_r"
                                      color={baseColors.label_assistive_1}
                                      pt="2px"
                                    >
                                      {i.note}
                                    </Box>
                                  )}
                                </Td>
                                <Td {...cellProps} isNumeric fontWeight={600} whiteSpace="nowrap">
                                  {i.price.toLocaleString()}원
                                </Td>
                              </Tr>
                            ))}
                        </Tbody>
                      </Table>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </BaseStack>
  );
}

/** 사용처에서 isPriceDisclosureParlor 로 감싼다 (비공개 이력, 폐업 제외) */
function ParlorPriceDisclosureSection({ parlor }: { parlor: ParlorDetailProps }) {
  const { binsos, services } = getParlorPriceDisclosure(parlor);
  const name = parlor.companyname;
  const esky = parlor.esky_price;
  const hasPrice = binsos.length > 0 || services.length > 0 || Boolean(esky?.items.length);

  return (
    <BaseStack width="100%" px={16} py={24} color={baseColors.label_normal}>
      <h2 className="headline_2_sb">{`${name} 가격 정보`}</h2>
      {/* 고지는 접지 않는다. 가격표를 펼치지 않은 사람에게도 출처가 보여야 한다 */}
      {hasPrice && (
        <Box className="caption_1_r" color={baseColors.label_assistive_1} pt="4px">
          {PRICE_SOURCE_NOTICE}
        </Box>
      )}
      <Spacing gap={16} />

      <BaseStack spacing={8}>
        {binsos.length > 0 && (
          <PriceAccordion
            parlorId={parlor.id}
            trigger="acc_price_binso"
            title="빈소 사용료"
            meta={`총 ${binsos.length}실, 1일 ${binsos[0].charge}부터`}
          >
            <Box
              border={`1px solid ${baseColors.line_normal}`}
              borderRadius="8px"
              overflow="hidden"
            >
              <Table size="sm" className="label_1_r">
                <Thead>
                  <Tr>
                    <Th {...headCellProps}>빈소명</Th>
                    <Th {...headCellProps}>평형</Th>
                    <Th {...headCellProps} isNumeric>
                      1일 사용료
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {binsos.map((b, i) => (
                    <Tr key={i} _last={{ td: { borderBottom: "none" } }}>
                      <Td {...cellProps}>{b.name}</Td>
                      <Td {...cellProps} color={baseColors.label_alternative_1}>
                        {b.pyeong}
                      </Td>
                      <Td {...cellProps} isNumeric fontWeight={600}>
                        {b.charge}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </PriceAccordion>
        )}

        {services.length > 0 && (
          <PriceAccordion
            parlorId={parlor.id}
            trigger="acc_price_fee"
            title="시설 이용료"
            meta={`${services.map((s) => s.label.replace(/ \(.*\)/, "")).join(", ")}`}
          >
            <Box
              border={`1px solid ${baseColors.line_normal}`}
              borderRadius="8px"
              overflow="hidden"
            >
              <Table size="sm" className="label_1_r">
                <Tbody>
                  {services.map((s) => (
                    <Tr
                      key={s.label}
                      _last={{ th: { borderBottom: "none" }, td: { borderBottom: "none" } }}
                    >
                      <Th
                        {...cellProps}
                        scope="row"
                        fontWeight={500}
                        color={baseColors.label_alternative_1}
                      >
                        {s.label}
                      </Th>
                      <Td {...cellProps} isNumeric fontWeight={600}>
                        {s.charge}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </PriceAccordion>
        )}

        {esky && esky.items.length > 0 && (
          <PriceAccordion
            parlorId={parlor.id}
            trigger="acc_price_esky"
            title="식사, 장례용품 비용"
            meta={`${[...new Set(esky.items.map((i) => i.group))]
              .sort(
                (a, b) =>
                  (ESKY_GROUP_ORDER.indexOf(a) + 1 || 99) - (ESKY_GROUP_ORDER.indexOf(b) + 1 || 99),
              )
              .slice(0, 4)
              .join(", ")} 등 ${esky.items.length}개 품목`}
          >
            {/*
              기준일은 장례식장이 등록한 날이 아니라 우리가 e하늘에서 받은 날 (독사운드와 같은 기준).
              등록일은 2025년처럼 오래된 곳이 많고, 받은 날은 다시 수집하면 갱신된다
            */}
            <Box className="caption_1_r" color={baseColors.label_assistive_1} pb="8px">
              e하늘 장사정보시스템에서{" "}
              <time dateTime={esky.collected_at}>{formatKoreanDate(esky.collected_at)}</time> 확인한
              가격입니다.
            </Box>
            <EskyPriceGroups data={esky} />
          </PriceAccordion>
        )}
      </BaseStack>
    </BaseStack>
  );
}

/**
 * 데이터 기반 FAQ. 가격 영역 한가운데 있으면 흐름을 끊어서 아래 가이드북 섹션 위로 옮겼다(2026-10-08).
 * FAQPage JSON-LD 는 pages/parlor/[id] 에서 그대로 넣는다. 질문마다 접어 두고, 처음 펼칠 때 기존 acc_price_faq 를 보낸다
 */
export function ParlorPriceFaqList({ parlor }: { parlor: ParlorDetailProps }) {
  const { faqs } = getParlorPriceDisclosure(parlor);
  if (faqs.length === 0) return null;

  return (
    <BaseStack as="dl" spacing={8}>
      {faqs.map((faq) => (
        <Box
          key={faq.question}
          as="details"
          borderRadius="8px"
          bg={baseColors.fill_normal}
          sx={{
            "& > summary::-webkit-details-marker": { display: "none" },
            "& .faq-chevron": { transform: "rotate(90deg)", transition: "transform 0.2s" },
            "&[open] .faq-chevron": { transform: "rotate(-90deg)" },
          }}
        >
          <Box
            as="summary"
            cursor="pointer"
            listStyleType="none"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap="8px"
            p="12px"
            onClick={(e: MouseEvent<HTMLElement>) => {
              const details = e.currentTarget.parentElement as HTMLDetailsElement | null;
              if (details?.open) return;
              customEvent({
                newGtm: {
                  path: "parlor_detail",
                  trigger_and_target: "acc_price_faq",
                  properties: { parlor_id: parlor.id },
                },
              });
            }}
          >
            <Box as="dt" className="body_2_normal_sb">
              <Box as="span" color={baseColors.orange_500} pr="4px">
                Q.
              </Box>
              {faq.question}
            </Box>
            <ColorIcon
              className="faq-chevron"
              name="icon-arrow-right-small-mono"
              color={baseColors.label_assistive_1}
              size={18}
            />
          </Box>
          <Box
            as="dd"
            className="body_2_normal_r"
            color={baseColors.label_alternative_1}
            px="12px"
            pb="12px"
          >
            {faq.answer}
          </Box>
        </Box>
      ))}
    </BaseStack>
  );
}

export default ParlorPriceDisclosureSection;
